/**
 * Build a page number list with ellipsis for large ranges.
 * Returns an array of 0-based page numbers and '...' placeholders.
 * Shows up to 7 items: first, last, current ±1, with ellipsis gaps.
 *
 * Lives in its own module (not inside PaginationControls.tsx) so the
 * ESLint `react-refresh/only-export-components` rule stays happy —
 * Vite/HMR only fast-refreshes module files that export *only*
 * components. Co-locating the helper alongside its sole consumer at
 * the directory level keeps it grep-discoverable without breaking the
 * fast-refresh contract; the unit test in PaginationControls.test.tsx
 * imports both via the same path.
 *
 * Preconditions (enforced by the caller, not re-validated here):
 *   - `totalPages >= 1`. The component returns `null` for `<= 1`
 *     before this function is reached, so a 0 or negative input is
 *     unreachable from production code and a deliberate no-op input
 *     for tests.
 *   - `currentPage` is a 0-based index in `[0, totalPages - 1]`.
 *
 * @param totalPages - Total number of pages (must be >= 1)
 * @param currentPage - Current 0-based page index
 * @returns Array of page numbers and '...' strings
 */
export function buildPageNumbers(totalPages: number, currentPage: number): (number | '...')[] {
  const pages: (number | '...')[] = []
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i)
  } else {
    pages.push(0)
    if (currentPage > 2) pages.push('...')
    for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages - 2, currentPage + 1); i++) {
      pages.push(i)
    }
    if (currentPage < totalPages - 3) pages.push('...')
    pages.push(totalPages - 1)
  }
  return pages
}
